import { Alert, Box, styled, Typography } from "@mui/material";
import React, { memo, useCallback } from "react";
import { Template } from "../../../interfaces/all";
import { ParameterConfig } from "../../../types/template-config";

const BoxWrapper = styled(Box)`
  background: #ffffff;
  padding: 12px 16px;
  margin-bottom: 1px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const TemplatePreview: React.FC<{
  selectedTemplate: Template | null;
  templateParameters: any;
  parameterConfigs: ParameterConfig[];
  attendantName: string;
}> = memo(
  ({
    selectedTemplate,
    templateParameters,
    parameterConfigs,
    attendantName,
  }) => {
    // Função para gerar preview completo do template
    const generateTemplatePreview = useCallback(
      (template: Template, configs: ParameterConfig[]) => {
        if (!template.components) return null;

        const preview = {
          header: null as any,
          body: null as any,
          footer: null as any,
          buttons: [] as any[],
        };

        template.components.forEach((component: any) => {
          switch (component.type) {
            case "HEADER": {
              if (component.format === "TEXT") {
                let headerText = component.text ?? "";

                configs.forEach((config, index) => {
                  const regex = new RegExp(`\\{\\{${index + 1}\\}\\}`, "g");

                  if (config.type === "fixed") {
                    headerText = headerText.replace(regex, config.value);
                  } else {
                    let value = "";

                    switch (config.dynamicField) {
                      case "clientName":
                        value = "[Nome do Cliente]";
                        break;
                      case "clientPhone":
                        value = "[Telefone do Cliente]";
                        break;
                      case "companyName":
                        value = "[Nome da Empresa]";
                        break;
                      case "attendantName":
                        value = attendantName ?? "[Nome do Atendente]";
                        break;
                      case "scheduledDate":
                        value = "[Data do Agendamento]";
                        break;
                      default:
                        value = "[Valor]";
                    }

                    headerText = headerText.replace(regex, value);
                  }
                });

                preview.header = { type: "text", text: headerText };
              } else if (component.format === "IMAGE") {
                preview.header = {
                  type: "image",
                  imageUrl: component.example?.header_handle?.[0] ?? null,
                  text: component.text ?? "",
                };
              }

              break;
            }

            case "BODY": {
              let bodyText = component.text ?? "";

              configs.forEach((config, index) => {
                const regex = new RegExp(`\\{\\{${index + 1}\\}\\}`, "g");

                if (config.type === "fixed") {
                  bodyText = bodyText.replace(regex, config.value);
                } else {
                  let value = "";

                  switch (config.dynamicField) {
                    case "clientName":
                      value = "[Nome do Cliente]";
                      break;
                    case "clientPhone":
                      value = "[Telefone do Cliente]";
                      break;
                    case "companyName":
                      value = "[Nome da Empresa]";
                      break;
                    case "attendantName":
                      value = attendantName ?? "[Nome do Atendente]";
                      break;
                    case "scheduledDate":
                      value = "[Data do Agendamento]";
                      break;
                    default:
                      value = "[Valor]";
                  }

                  bodyText = bodyText.replace(regex, value);
                }
              });

              preview.body = { text: bodyText };
              break;
            }

            case "FOOTER": {
              preview.footer = { text: component.text ?? "" };
              break;
            }

            case "BUTTONS": {
              if (component.buttons) {
                preview.buttons = component.buttons.map((button: any) => ({
                  type: button.type,
                  text: button.text,
                }));
              }
              break;
            }
          }
        });

        return preview;
      },
      [attendantName],
    );

    if (!selectedTemplate || !templateParameters) {
      return null;
    }

    const preview = generateTemplatePreview(selectedTemplate, parameterConfigs);

    if (!preview) {
      return (
        <BoxWrapper>
          <Alert severity="warning">
            Não foi possível gerar o preview do template.
          </Alert>
        </BoxWrapper>
      );
    }

    return (
      <BoxWrapper
        sx={{ backgroundColor: "#f0f2f5", p: 3, borderRadius: "12px" }}
      >
        <Box
          sx={{
            position: "relative",
            backgroundColor: "#efe7de",
            backgroundImage: `url('/C:/Users/Allan/.gemini/antigravity/brain/f0be8291-6937-41ba-a263-a1de1967d8bc/whatsapp_doodle_bg_1776925041642.png')`,
            backgroundSize: "400px",
            backgroundRepeat: "repeat",
            borderRadius: "16px",
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start", // Mensagem recebida (templates geralmente são mostrados como recebidos/enviados pelo sistema)
            overflow: "hidden",
            boxShadow: "inset 0 0 100px rgba(0,0,0,0.05)",
            maxWidth: "450px",
            mx: "auto",
          }}
        >
          {/* Overlay suave */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(239, 231, 222, 0.4)",
              zIndex: 0,
            }}
          />

          {/* Balão de Mensagem (Branco para recebida) */}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              backgroundColor: "#ffffff",
              p: "6px 8px 8px 8px",
              borderRadius: "0px 8px 8px 8px",
              maxWidth: "90%",
              boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
              display: "flex",
              flexDirection: "column",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "-8px",
                width: 0,
                height: 0,
                borderTop: "10px solid #ffffff",
                borderLeft: "10px solid transparent",
              },
            }}
          >
            {/* Header do Template */}
            {preview.header && (
              <Box sx={{ mb: 1, borderRadius: "6px", overflow: "hidden" }}>
                {preview.header.type === "image" ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "180px",
                      backgroundColor: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {preview.header.imageUrl ? (
                      <img
                        src={preview.header.imageUrl}
                        alt="Header"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        📷 Imagem do Header
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: "#111b21", p: 0.5 }}
                  >
                    {preview.header.text}
                  </Typography>
                )}
              </Box>
            )}

            {/* Body do Template */}
            {preview.body && (
              <Box sx={{ px: 0.5, pb: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#111b21",
                    fontSize: "14px",
                    lineHeight: 1.45,
                    whiteSpace: "pre-wrap",
                    fontFamily:
                      "Segoe UI, Helvetica Neue, Helvetica, Lucida Grande, Arial, Ubuntu, Cantarell, Fira Sans, sans-serif",
                  }}
                >
                  {preview.body.text}
                </Typography>
              </Box>
            )}

            {/* Footer do Template */}
            {preview.footer && (
              <Box sx={{ px: 0.5, mt: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#667781", fontSize: "12px" }}
                >
                  {preview.footer.text}
                </Typography>
              </Box>
            )}

            {/* Hora da Mensagem */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 0.25,
                pr: 0.5,
              }}
            >
              <Typography sx={{ fontSize: "11px", color: "#667781" }}>
                {new Date().toLocaleTimeString("pt-BR", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Typography>
            </Box>

            {/* Botões do Template (WhatsApp Buttons) */}
            {preview.buttons && preview.buttons.length > 0 && (
              <Box
                sx={{
                  mt: 1,
                  borderTop: "1px solid #f0f0f0",
                  mx: -1,
                  mb: -1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {preview.buttons.map((button: any, index: number) => (
                  <Box
                    key={index}
                    sx={{
                      p: 1.25,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      borderBottom:
                        index === preview.buttons.length - 1
                          ? "none"
                          : "1px solid #f0f0f0",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#f8f9fa" },
                      transition: "background-color 0.2s",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#00a884",
                        fontSize: "14px",
                        fontWeight: 500,
                        textAlign: "center",
                      }}
                    >
                      {button.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </BoxWrapper>
    );
  },
);

TemplatePreview.displayName = "TemplatePreview";

export default TemplatePreview;
